import React from "react";
import { Box, Container } from "@mui/material";
import { colors } from "../../styles/theme";
import { useEditMode } from "./ProjectLayout";
import { Windpark, PageType } from "../../types";
import ProjectOverviewPage from "../pages/projectOverview/ProjectOverviewPage";
import ProjectCompanyPage from "../pages/projectCompany/ProjectCompanyPage";
import AreaPage from "../pages/projectArea/AreaPage";
import GenerationUnitsPage from "../pages/projectGenerationUnits/GenerationUnitsPage";
import YieldPage from "../pages/projectYield/YieldPage";
import TariffsPage from "../pages/projectTariffs/TariffsPage";
import CostsPage from "../pages/projectCosts/CostsPage";
import FinancingPage from "../pages/projectFinancing/FinancingPage";
import CalculationPage from "../pages/projectCalculation/CalculationPage";
import PermitApplicationsPage from "../pages/projectPermits/PermitApplicationsPage";
import PermitEvidencesPage from "../pages/projectPermits/PermitEvidencesPage";
import PermitCostsPage from "../pages/projectPermits/PermitCostsPage";

interface ProjectContentProps {
  currentPage: PageType;
  /** Antrag der aktuellen Seite (Seiten unter einem Antrag im Modul Genehmigung) */
  procedureId: string | null;
  currentProject: Windpark;
  canView: boolean;
  canEdit: boolean;
  /** Callback wenn Projekt-Daten aktualisiert wurden (z.B. aktiver Ertragswert) */
  onProjectUpdated?: () => void;
}

/**
 * Project Content Component
 * Definiert den hellen blauen Hintergrund und Container für Projekt-Inhalte
 */
const ProjectContent: React.FC<ProjectContentProps> = ({
  currentPage,
  procedureId,
  currentProject,
  canView,
  canEdit,
  onProjectUpdated,
}) => {
  // Rendere die entsprechende Seite basierend auf currentPage
  const renderCurrentPage = () => {
    switch (currentPage) {
      case "project-overview":
        return (
          <ProjectOverviewPage
            project={currentProject}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={0}
            canView={canView}
            canEdit={canEdit}
            onProjectUpdated={onProjectUpdated}
          />
        );
      case "project-company":
        return (
          <ProjectCompanyPage
            project={currentProject}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={1}
            canView={canView}
            canEdit={canEdit}
          />
        );
      // Liste aller Anträge und ELiA-Sicht eines Antrags sind dieselbe Seitenkomponente: Beim
      // Öffnen eines frisch angelegten Antrags bleiben so Daten und Zustand stehen.
      case "project-permit-applications":
      case "project-permit-elia":
        return (
          <PermitApplicationsPage
            project={currentProject}
            procedureId={procedureId}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={currentPage === "project-permit-elia" ? 12 : 2}
            canView={canView}
            canEdit={canEdit}
          />
        );
      case "project-permit-evidences":
        return procedureId ? (
          <PermitEvidencesPage
            project={currentProject}
            procedureId={procedureId}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={10}
            canView={canView}
            canEdit={canEdit}
          />
        ) : null;
      case "project-permit-costs":
        return procedureId ? (
          <PermitCostsPage
            project={currentProject}
            procedureId={procedureId}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={11}
            canView={canView}
            canEdit={canEdit}
          />
        ) : null;
      case "project-area":
        return (
          <AreaPage
            project={currentProject}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={3}
            canView={canView}
            canEdit={canEdit}
          />
        );
      case "project-generation-units":
        return (
          <GenerationUnitsPage
            project={currentProject}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={4}
            canView={canView}
            canEdit={canEdit}
          />
        );
      case "project-yield":
        return (
          <YieldPage
            project={currentProject}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={5}
            canView={canView}
            canEdit={canEdit}
            onProjectUpdated={onProjectUpdated}
          />
        );
      case "project-revenues":
        return (
          <TariffsPage
            project={currentProject}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={6}
            canView={canView}
            canEdit={canEdit}
          />
        );
      case "project-costs":
        return (
          <CostsPage
            project={currentProject}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={7}
            canView={canView}
            canEdit={canEdit}
          />
        );
      case "project-financing":
        return (
          <FinancingPage
            project={currentProject}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={8}
            canView={canView}
            canEdit={canEdit}
          />
        );
      case "project-calculation":
        return (
          <CalculationPage
            project={currentProject}
            onBack={() => {}}
            onTabChange={() => {}}
            currentTab={9}
            canView={canView}
            canEdit={canEdit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: "transparent",
      }}
    >
      <Container maxWidth={false}>{renderCurrentPage()}</Container>
    </Box>
  );
};

export default ProjectContent;
